// frontend/src/components/formularios/SelectorObjetivosPadre.jsx
import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Alert,
  ListSubheader,
  CircularProgress
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import formularioService from '../../services/formularioService';

const SelectorObjetivosPadre = ({ 
  formularioPadre, 
  objetivosSeleccionados = [], 
  onSeleccionarObjetivo,
  configuracion = {},
  disabled = false
}) => {
  const [objetivosDisponibles, setObjetivosDisponibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [objetivosPorArea, setObjetivosPorArea] = useState({});
  
  useEffect(() => {
    if (formularioPadre) {
      cargarObjetivos();
    }
  }, [formularioPadre, objetivosSeleccionados]);
  
  const cargarObjetivos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Obtener objetivos disponibles del backend
      const response = await formularioService.getObjetivosDisponibles(formularioPadre.id);
      
      if (response.success && response.data) {
        const { objetivos_disponibles, objetivos_usados, objetivos_libres } = response.data;
        
        // Usar objetivos libres si no se quieren repetir
        const objetivosAMostrar = configuracion.no_repetir ? 
          objetivos_libres : objetivos_disponibles;
        
        // Agrupar por área si está configurado
        if (configuracion.mostrar_area) {
          const agrupados = {};
          objetivosAMostrar.forEach(obj => {
            const areaNombre = obj.area_nombre || 'Sin área';
            if (!agrupados[areaNombre]) {
              agrupados[areaNombre] = [];
            }
            agrupados[areaNombre].push(obj);
          });
          setObjetivosPorArea(agrupados);
        }
        
        setObjetivosDisponibles(objetivosAMostrar);
      }
    } catch (error) {
      console.error('Error al cargar objetivos:', error);
      setError('Error al cargar los objetivos disponibles');
      
      // Fallback: extraer objetivos directamente de la respuesta
      procesarObjetivosLocal();
    } finally {
      setLoading(false);
    }
  };
  
  const procesarObjetivosLocal = () => {
    if (!formularioPadre.respuestas) {
      setObjetivosDisponibles([]);
      return;
    }
    
    const areas = formularioPadre.respuestas.areas_intervencion || 
                 formularioPadre.respuestas.areas_intervencion_neuro || [];
    
    const todosObjetivos = [];
    const agrupados = {};
    
    areas.forEach((area, areaIndex) => {
      const areaNombre = area.area || area.area_intervenir || `Área ${areaIndex + 1}`;
      const objetivosTexto = area.objetivos_especificos || '';
      const listaObjetivos = objetivosTexto
        .split('\n')
        .filter(obj => obj.trim())
        .map(obj => obj.trim());
      
      if (!agrupados[areaNombre]) {
        agrupados[areaNombre] = [];
      }
      
      listaObjetivos.forEach((objetivo, objIndex) => {
        const objetivoId = `area_${areaIndex}_obj_${objIndex}`;
        
        // Verificar si ya fue seleccionado
        if (configuracion.no_repetir && objetivosSeleccionados.includes(objetivoId)) {
          return;
        }
        
        const objetivoObj = {
          id: objetivoId,
          area_nombre: areaNombre,
          area_numero: area.numero || areaIndex + 1,
          objetivo_texto: objetivo,
          objetivo_completo: configuracion.mostrar_area ? 
            `${areaNombre} - ${objetivo}` : objetivo
        };
        
        todosObjetivos.push(objetivoObj);
        agrupados[areaNombre].push(objetivoObj);
      });
    });
    
    setObjetivosDisponibles(todosObjetivos);
    if (configuracion.mostrar_area) {
      setObjetivosPorArea(agrupados);
    }
  };
  
  const handleChange = (event) => {
    const objetivoId = event.target.value;
    onSeleccionarObjetivo(objetivoId);
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  
  if (error) {
    return <Alert severity="warning">{error}</Alert>;
  }
  
  if (objetivosDisponibles.length === 0) {
    return (
      <Alert severity="info">
        {configuracion.no_repetir ? 
          'Todos los objetivos han sido trabajados' : 
          'No hay objetivos disponibles'}
      </Alert>
    );
  }
  
  return (
    <FormControl fullWidth margin="normal">
      <InputLabel id="selector-objetivos-label">
        Seleccionar Objetivo Específico
        <Typography component="span" color="error">*</Typography>
      </InputLabel>
      <Select
        labelId="selector-objetivos-label"
        value=""
        onChange={handleChange}
        label="Seleccionar Objetivo Específico"
        disabled={disabled}
      >
        <MenuItem value="" disabled>
          <em>Seleccione un objetivo a trabajar</em>
        </MenuItem>
        
        {configuracion.mostrar_area && Object.keys(objetivosPorArea).length > 0 ? (
          // Mostrar agrupado por áreas
          Object.entries(objetivosPorArea).map(([areaNombre, objetivos]) => [
            <ListSubheader key={`header-${areaNombre}`}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2" color="primary">
                  {areaNombre}
                </Typography>
                <Chip 
                  size="small" 
                  label={`${objetivos.length} objetivo${objetivos.length > 1 ? 's' : ''}`}
                />
              </Box>
            </ListSubheader>,
            ...objetivos.map(obj => (
              <MenuItem 
                key={obj.id} 
                value={obj.id}
                sx={{ pl: 4 }}
              >
                <Box display="flex" alignItems="center" gap={1} width="100%">
                  <Typography variant="body2" flex={1}>
                    {obj.objetivo_texto}
                  </Typography>
                  {objetivosSeleccionados.includes(obj.id) && (
                    <Chip 
                      icon={<CheckCircleIcon />}
                      label="Seleccionado"
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Box>
              </MenuItem>
            ))
          ])
        ) : (
          // Mostrar lista simple
          objetivosDisponibles.map(obj => (
            <MenuItem key={obj.id} value={obj.id}>
              <Box display="flex" alignItems="center" gap={1} width="100%">
                <Typography variant="body2" flex={1}>
                  {obj.objetivo_completo || obj.objetivo_texto}
                </Typography>
                {objetivosSeleccionados.includes(obj.id) && (
                  <Chip 
                    icon={<CheckCircleIcon />}
                    label="Trabajado"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Box>
            </MenuItem>
          ))
        )}
      </Select>
      
      {configuracion.ayuda && (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
          {configuracion.ayuda}
        </Typography>
      )}
    </FormControl>
  );
};

export default SelectorObjetivosPadre;